import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingAPI } from "../../services/onboarding.service";
import { refreshOnboardingCache } from "../../components/RequireAuth";
import { ComplianceAPI, ClinicDocType } from "../../services/compliance.service";
import KaagazyLogoWhite from "../../assets/logo-white.png";
import KaagazyLogoMobile from "../../assets/logo-teal-mobile.png";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

// Clinic credentialing documents — the same 3 clinic-sourced items
// Settings → Compliance & Verification groups as "Clinic credentialing
// documents" (the 4th, Medical Director's NMC Registration, is doctor-
// sourced and isn't collectible yet at this point in signup, since no
// doctor profile exists until later). These are what app/core/authz.py
// actually gates `can_prescribe` on — unlike the old business-registration
// set (incorporation/GST/MSME/etc.) this page used to ask for, which never
// gated anything.
type DocumentId = "aadhaar" | "pan" | "cea";

type UploadStatus = "pending" | "selected" | "uploading" | "uploaded" | "error";

const DOC_ID_TO_TYPE: Record<DocumentId, ClinicDocType> = {
  aadhaar: "owner_aadhaar",
  pan: "owner_pan",
  cea: "clinical_establishment_reg",
};

const TYPE_TO_DOC_ID: Record<string, DocumentId> = {
  owner_aadhaar: "aadhaar",
  owner_pan: "pan",
  clinical_establishment_reg: "cea",
};

interface DocumentMeta {
  id: DocumentId;
  label: string;
  required: boolean;
  description: string;
}

interface UploadedFile {
  file: File | null;
  url?: string;
  s3Key?: string;
  fileName?: string;
  kycDocId?: string;
  status: UploadStatus;
  progress: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DOCUMENTS: DocumentMeta[] = [
  {
    id: "aadhaar",
    label: "Director / Owner Aadhaar",
    required: true,
    description: "Required • Aadhaar card of the clinic director or proprietor • PDF or Image (Max 5MB)",
  },
  {
    id: "pan",
    label: "Director / Owner PAN Card",
    required: true,
    description: "Required • PAN card of the clinic director or proprietor • PDF or Image (Max 5MB)",
  },
  {
    id: "cea",
    label: "Clinical Establishment Certificate",
    required: false,
    description: "Optional • Registration under CEA 2010 or State equivalent • PDF or Image (Max 5MB)",
  },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

/* ------------------------------------------------------------------ */
/*  SVG Icons (utility)                                                */
/* ------------------------------------------------------------------ */

const UploadCloudIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const FileTextIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const EyeOffIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Left-panel artwork — clinic/building themed                        */
/* ------------------------------------------------------------------ */

const ClinicBuildingArt: React.FC = () => (
  <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[260px]">
    {/* Ground shadow */}
    <ellipse cx="100" cy="178" rx="62" ry="8" fill="rgba(0,0,0,0.12)" />

    {/* Main building body */}
    <rect x="30" y="70" width="140" height="104" rx="4" fill="white" fillOpacity="0.18" />
    <rect x="30" y="70" width="140" height="104" rx="4" stroke="white" strokeOpacity="0.35" strokeWidth="1.5" />

    {/* Roof / header bar */}
    <rect x="22" y="58" width="156" height="18" rx="4" fill="white" fillOpacity="0.28" />

    {/* Clinic cross sign on roof */}
    <rect x="92" y="62" width="16" height="10" rx="2" fill="white" fillOpacity="0.6" />
    <rect x="95" y="59" width="10" height="16" rx="2" fill="white" fillOpacity="0.6" />

    {/* Windows row 1 */}
    <rect x="44" y="84" width="26" height="20" rx="3" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
    <rect x="87" y="84" width="26" height="20" rx="3" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
    <rect x="130" y="84" width="26" height="20" rx="3" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.3" strokeWidth="1" />

    {/* Windows row 2 */}
    <rect x="44" y="114" width="26" height="20" rx="3" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
    <rect x="87" y="114" width="26" height="20" rx="3" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
    <rect x="130" y="114" width="26" height="20" rx="3" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.3" strokeWidth="1" />

    {/* Door */}
    <rect x="82" y="144" width="36" height="30" rx="3" fill="white" fillOpacity="0.15" stroke="white" strokeOpacity="0.3" strokeWidth="1" />
    <circle cx="114" cy="159" r="2" fill="white" fillOpacity="0.5" />

    {/* Document / certificate floating right */}
    <g transform="translate(148, 30)">
      <rect x="0" y="0" width="34" height="42" rx="4" fill="white" fillOpacity="0.22" stroke="white" strokeOpacity="0.4" strokeWidth="1.2" />
      <line x1="6" y1="12" x2="28" y2="12" stroke="white" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="6" y1="19" x2="28" y2="19" stroke="white" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="6" y1="26" x2="20" y2="26" stroke="white" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" />
      {/* Seal */}
      <circle cx="23" cy="33" r="6" fill="white" fillOpacity="0.25" stroke="white" strokeOpacity="0.5" strokeWidth="1" />
      <polyline points="20,33 22,35 26,30" stroke="white" strokeOpacity="0.7" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </g>

    {/* Stamp / seal floating left */}
    <g transform="translate(14, 22)">
      <circle cx="18" cy="18" r="16" fill="white" fillOpacity="0.14" stroke="white" strokeOpacity="0.35" strokeWidth="1.2" />
      <circle cx="18" cy="18" r="10" fill="none" stroke="white" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2.5 2" />
      <text x="18" y="22" textAnchor="middle" fill="white" fillOpacity="0.55" fontSize="10" fontWeight="bold">✓</text>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Document-type icons (coloured)                                     */
/* ------------------------------------------------------------------ */

const AadhaarIcon: React.FC = () => (
  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4f46e5] to-[#007cf0] flex items-center justify-center shadow-sm flex-shrink-0">
    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18M3 9h6M3 15h6" />
      <line x1="13" y1="8" x2="19" y2="8" />
      <line x1="13" y1="12" x2="19" y2="12" />
      <line x1="13" y1="16" x2="16" y2="16" />
    </svg>
  </div>
);

const PanIcon: React.FC = () => (
  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0891b2] to-[#00b3ff] flex items-center justify-center shadow-sm flex-shrink-0">
    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M9 13h1.5a1.5 1.5 0 010 3H9v-3zm0 3v2" />
      <line x1="14" y1="13" x2="17" y2="13" />
      <line x1="14" y1="17" x2="15.5" y2="17" />
    </svg>
  </div>
);

const CeaIcon: React.FC = () => (
  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007cf0] to-[#00dfd8] flex items-center justify-center shadow-sm flex-shrink-0">
    <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M12 8v4M10 10h4" />
      <line x1="7" y1="16" x2="17" y2="16" />
      <line x1="7" y1="13" x2="10" y2="13" />
    </svg>
  </div>
);

const DOCUMENT_ICONS: Record<DocumentId, React.FC> = {
  aadhaar: AadhaarIcon,
  pan: PanIcon,
  cea: CeaIcon,
};

/* ------------------------------------------------------------------ */
/*  DocumentCard sub-component                                         */
/* ------------------------------------------------------------------ */

interface DocumentCardProps {
  doc: DocumentMeta;
  uploadedFile: UploadedFile | null;
  onFileSelect: (docId: DocumentId, file: File) => void;
  onRemove: (docId: DocumentId) => void;
  onView: (docId: DocumentId) => void;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ doc, uploadedFile, onFileSelect, onRemove, onView }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const IconComponent = DOCUMENT_ICONS[doc.id];
  const status = uploadedFile?.status ?? "pending";

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(doc.id, file);
  }, [doc.id, onFileSelect]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(doc.id, file);
    if (inputRef.current) inputRef.current.value = "";
  }, [doc.id, onFileSelect]);

  const statusBadge = (size: "sm" | "md") => {
    const px = size === "sm" ? "px-3 py-0.5 text-[10px]" : "px-4 py-1 text-xs";
    if (status === "pending") return <span className={`inline-flex items-center rounded-full border border-slate-200 ${px} font-medium text-slate-500`}>Pending</span>;
    if (status === "selected") return <span className={`inline-flex items-center rounded-full border border-slate-300 bg-slate-50 ${px} font-medium text-slate-600`}>Ready</span>;
    if (status === "uploading") return <span className={`inline-flex items-center rounded-full border border-blue-200 bg-blue-50 ${px} font-medium text-blue-600`}>Uploading...</span>;
    if (status === "uploaded") return <span className={`inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 ${px} font-medium text-green-600`}><CheckCircleIcon className="w-3 h-3" /> Uploaded</span>;
    if (status === "error") return <span className={`inline-flex items-center rounded-full border border-red-200 bg-red-50 ${px} font-medium text-red-600`}>Error</span>;
    return null;
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white px-5 py-5 lg:px-6 lg:py-7 shadow-card flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
      {/* Left: icon + label + mobile badge */}
      <div className="flex items-center gap-3 lg:gap-4 min-w-0">
        <IconComponent />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm lg:text-base font-medium text-slate-900 truncate">{doc.label}</h3>
            <div className="lg:hidden flex-shrink-0">{statusBadge("sm")}</div>
          </div>
          <p className="text-xs lg:text-sm text-slate-500 mt-0.5 lg:mt-1">{doc.description}</p>
        </div>
      </div>

      {/* Centre: status badge (desktop) */}
      <div className="hidden lg:block flex-shrink-0">{statusBadge("md")}</div>

      {/* Right: drop zone or file preview */}
      <div className="flex-shrink-0 w-full lg:w-auto">
        {(status === "selected" || status === "uploaded") && uploadedFile ? (
          <div className="w-full lg:w-[200px] rounded-xl border border-slate-200 bg-slate-50 p-3 lg:p-4 flex items-center gap-2">
            <FileTextIcon className={`w-5 h-5 flex-shrink-0 ${status === "uploaded" ? "text-green-500" : "text-slate-400"}`} />
            <span className="text-xs text-slate-600 truncate flex-1">
              {uploadedFile.file?.name ?? uploadedFile.fileName ?? "Uploaded file"}
            </span>
            {uploadedFile.url && (
              <button type="button" onClick={() => onView(doc.id)} className="text-slate-400 hover:text-blue-500 flex-shrink-0" title="View file">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </button>
            )}
            <button type="button" onClick={() => onRemove(doc.id)} className="text-slate-400 hover:text-red-500 flex-shrink-0">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ) : (
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`w-full lg:w-[200px] rounded-xl border-2 border-dashed p-3 lg:p-4 flex flex-col items-center gap-1.5 transition-colors cursor-pointer ${dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-slate-50/50"}`}
            onClick={() => inputRef.current?.click()}
          >
            <UploadCloudIcon className="w-6 h-6 text-slate-400" />
            <span className="text-[11px] text-slate-400">Drag and drop your file here</span>
            <span className="text-[11px] text-slate-400">or</span>
            <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-[#007cf0] to-[#00b3ff] px-4 py-1.5 text-[11px] font-medium text-white">
              <UploadCloudIcon className="w-3.5 h-3.5" /> Browse Files
            </span>
            <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFileChange} className="hidden" />
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  S3 upload helper                                                   */
/* ------------------------------------------------------------------ */

function uploadToS3(url: string, file: File, onProgress: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });
    xhr.addEventListener("load", () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`))
    );
    xhr.addEventListener("error", () => reject(new Error("Upload network error")));
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.send(file);
  });
}

/* ------------------------------------------------------------------ */
/*  Main page component                                                */
/* ------------------------------------------------------------------ */

const DocumentUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const [files, setFiles] = useState<Record<string, UploadedFile>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [clinicId, setClinicId] = useState<string | null>(null);

  const showError = (msg: string) => {
    setError(msg);
    rightPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Resolve clinic_id (set once the "What Do You Do?" step creates the
  // clinic, which always happens before this step), then load any
  // credentialing docs already uploaded (e.g. the user came back to this
  // step after a partial submit).
  useEffect(() => {
    let cancelled = false;
    OnboardingAPI.getStatus()
      .then(async ({ clinic_id }) => {
        if (cancelled || !clinic_id) return;
        setClinicId(clinic_id);
        const docs = await ComplianceAPI.listDocuments(clinic_id);
        if (cancelled) return;
        const initial: Record<string, UploadedFile> = {};
        for (const doc of docs) {
          const docId = TYPE_TO_DOC_ID[doc.document_type];
          if (!docId) continue; // e.g. drug_license/abdm_registration — not asked for here
          initial[docId] = {
            file: null,
            url: doc.view_url ?? undefined,
            s3Key: doc.s3_key,
            fileName: DOCUMENTS.find((d) => d.id === docId)?.label,
            status: "uploaded",
            progress: 100,
          };
        }
        if (Object.keys(initial).length > 0) setFiles(initial);
      })
      .catch(() => { /* no clinic/docs yet — start fresh */ });
    return () => { cancelled = true; };
  }, []);

  const uploadedCount = Object.values(files).filter((f) => f.status === "uploaded" || f.status === "selected").length;
  const requiredDocs = DOCUMENTS.filter((d) => d.required);
  const missingRequired = requiredDocs.filter((d) => files[d.id]?.status !== "uploaded" && files[d.id]?.status !== "selected").length;
  const totalRemaining = DOCUMENTS.filter((d) => files[d.id]?.status !== "uploaded" && files[d.id]?.status !== "selected").length;

  const handleFileSelect = useCallback((docId: DocumentId, file: File) => {
    setError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Only PDF and image files (PNG, JPG, WEBP) are accepted.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("File size must be under 5 MB.");
      return;
    }
    setFiles((prev) => ({ ...prev, [docId]: { file, status: "selected", progress: 0 } }));
  }, []);

  const handleRemove = useCallback((docId: DocumentId) => {
    setFiles((prev) => { const copy = { ...prev }; delete copy[docId]; return copy; });
  }, []);

  const handleView = useCallback(async (docId: DocumentId) => {
    if (!clinicId) return;
    try {
      const view_url = await ComplianceAPI.getViewUrl(clinicId, DOC_ID_TO_TYPE[docId]);
      if (view_url) window.open(view_url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Could not load document for viewing. Please try again.");
    }
  }, [clinicId]);

  // "Save as Draft" doubles as "Skip for now" — a draft with no documents
  // submitted is functionally identical to skipping, so there's no need for
  // both a no-op localStorage-only draft button and a separate skip button.
  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await OnboardingAPI.skipDocuments();
      await refreshOnboardingCache();
      navigate("/onboarding/select-plan", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Skip failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!clinicId) {
      showError("Clinic details are still loading — please try again in a moment.");
      return;
    }
    if (uploadedCount === 0) {
      showError("Please upload at least one document before submitting. Use 'Save as Draft' to continue without documents.");
      return;
    }
    if (missingRequired > 0) {
      showError(`Please upload all ${requiredDocs.length} required documents before submitting.`);
      return;
    }

    setSubmitting(true);
    try {
      const toUpload = (Object.entries(files) as [DocumentId, UploadedFile][])
        .filter(([, f]) => f.status === "selected" && f.file);

      if (toUpload.length > 0) {
        const presign = await ComplianceAPI.getPresignedUrls(clinicId);
        const confirmed: { document_type: ClinicDocType; s3_key: string }[] = [];

        // allSettled (not all): one failed upload must not lose the others.
        // Promise.all rejects on the first failure, so confirmUploads never
        // ran even for documents that had already finished uploading -
        // those objects were left orphaned in S3, and a retry re-presigned
        // fresh keys for everything, including the ones that actually
        // succeeded the first time.
        const results = await Promise.allSettled(
          toUpload.map(async ([docId, f]) => {
            const docType = DOC_ID_TO_TYPE[docId];
            const entry = presign.entries[docType];
            if (!entry) throw new Error(`No upload slot returned for ${docId}`);

            setFiles((prev) => ({ ...prev, [docId]: { ...prev[docId], status: "uploading", progress: 0 } }));

            if (presign.s3_enabled) {
              // A real S3 environment must always hand back an upload_url
              // for every entry (see backend generate_presigned_put_url) -
              // a missing one here means something went wrong, not "dev
              // mode", so it must not be silently confirmed as uploaded.
              if (!entry.upload_url) throw new Error(`No upload URL returned for ${docId}`);
              await uploadToS3(entry.upload_url, f.file as File, (p) =>
                setFiles((prev) => ({ ...prev, [docId]: { ...prev[docId], progress: p } }))
              );
            }
            // presign.s3_enabled === false: dev/test environment, the real
            // upload is intentionally skipped so onboarding stays testable
            // without live S3 - same convention Settings' compliance upload
            // already relies on.

            setFiles((prev) => ({ ...prev, [docId]: { ...prev[docId], status: "uploaded", progress: 100 } }));
            confirmed.push({ document_type: docType, s3_key: entry.s3_key });
          })
        );

        const failedLabels = results
          .map((r, i) => (r.status === "rejected" ? toUpload[i][0] : null))
          .filter((docId): docId is DocumentId => docId !== null)
          .map((docId) => {
            setFiles((prev) => ({ ...prev, [docId]: { ...prev[docId], status: "error", progress: 0 } }));
            return DOCUMENTS.find((d) => d.id === docId)?.label ?? docId;
          });

        if (confirmed.length > 0) {
          await ComplianceAPI.confirmUploads(clinicId, confirmed);
        }

        if (failedLabels.length > 0) {
          showError(`Failed to upload: ${failedLabels.join(", ")}. Please retry before submitting.`);
          return;
        }
      }

      // Advances onboarding_status (DOCUMENTS_PENDING -> EMAIL_VERIFICATION_PENDING)
      // server-side; re-validates owner_aadhaar/owner_pan are present via the
      // clinic_documents table the ComplianceAPI calls above just wrote to.
      await OnboardingAPI.submitDocuments();
      await refreshOnboardingCache();
      navigate("/onboarding/select-plan", { replace: true });
    } catch (err: any) {
      showError(err?.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative h-screen overflow-hidden flex">
      {/* Bottom gradient bleed */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{ background: "linear-gradient(to top, #00dfd8 0%, transparent 100%)", opacity: 0.15 }}
      />

      {/* ───── Left panel (40%) ───── */}
      <div
        className="hidden md:flex w-[40%] h-full flex-col relative"
        style={{ background: "linear-gradient(180deg, #4f46e5 0%, #007cf0 45%, #00dfd8 100%)" }}
      >
        <div className="absolute top-0 left-6">
          <img
            src={KaagazyLogoWhite}
            alt="Kaagazy"
            className="h-72 object-contain"
            style={{ mixBlendMode: "screen", marginTop: "-60px" }}
          />
        </div>

        <div className="flex flex-col justify-start pt-60 px-10 lg:px-14 pb-10 text-white">
          <p className="text-base font-normal text-white/60 mb-3">Step 2 of 3</p>

          <h1 className="text-[44px] font-bold leading-[1.1] tracking-tight">
            Clinic
            <br />
            Documents
          </h1>

          <p className="text-[17px] text-white/70 mt-6 leading-relaxed max-w-[360px]">
            Upload your clinic's official registration and compliance documents.
            These verify your clinic's legal standing and help us onboard you
            faster.
          </p>

          {/* Upload progress */}
          <div className="mt-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-5 max-w-[360px]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[15px] font-medium text-white/90">Upload Progress</span>
              <span className="text-[15px] font-semibold text-white">{uploadedCount}/{DOCUMENTS.length}</span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full rounded-full bg-white transition-all duration-500"
                style={{ width: `${(uploadedCount / DOCUMENTS.length) * 100}%` }}
              />
            </div>
            <p className="text-sm text-white/50 mt-3">
              {totalRemaining > 0
                ? `${totalRemaining} document${totalRemaining > 1 ? "s" : ""} remaining`
                : "All documents uploaded"}
            </p>
          </div>

          {/* Illustration */}
          <div className="mt-10 flex justify-center opacity-80">
            <ClinicBuildingArt />
          </div>
        </div>
      </div>

      {/* ───── Right panel (60%) ───── */}
      <div ref={rightPanelRef} className="flex flex-col w-full md:w-[60%] bg-white h-full overflow-y-auto relative z-10 items-center">
        <div className="flex-1 px-4 sm:px-6 md:px-12 py-6 sm:py-10 md:py-12 max-w-[780px] w-full">
          {/* Mobile-only branding */}
          <div className="md:hidden mb-4 flex justify-center">
            <img
              src={KaagazyLogoMobile}
              alt="Kaagazy"
              className="h-10 object-contain"
            />
          </div>

          {/* Mobile step indicator */}
          <div className="md:hidden mb-4">
            <p className="text-xs font-medium text-slate-400">Step 2 of 3</p>
          </div>

          {/* Header */}
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-1">Clinic Credentialing Documents</h2>
          <p className="text-xs sm:text-sm text-slate-500 mb-6 sm:mb-8">
            Upload documents to verify your clinic and enable prescribing for your team
          </p>

          {/* Error alert */}
          {error && (
            <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-[11px] text-red-600">
              {error}
            </div>
          )}

          {/* Document cards */}
          <div className="space-y-4">
            {DOCUMENTS.map((doc) => (
              <DocumentCard
                key={doc.id}
                doc={doc}
                uploadedFile={files[doc.id] ?? null}
                onFileSelect={handleFileSelect}
                onRemove={handleRemove}
                onView={handleView}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            <button
              type="button"
              onClick={handleSkip}
              disabled={submitting}
              className="rounded-full border border-slate-300 bg-white px-6 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed order-2 sm:order-1"
            >
              {submitting ? "Saving..." : "Save as Draft"}
            </button>

            <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 order-1 sm:order-2">
              <button
                type="button"
                onClick={() => navigate("/onboarding/clinic-details")}
                className="text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >
                Back
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#4f46e5] to-[#007cf0] px-5 sm:px-7 py-2.5 text-sm font-medium text-white shadow-sm hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit for Verification"}
                {!submitting && <CheckCircleIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Tips */}
          <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-4 sm:pt-6">
            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 sm:py-4 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FileTextIcon className="w-5 h-5 text-[#007cf0]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Clear Quality</p>
                <p className="text-xs text-slate-400 mt-0.5">Ensure text is readable</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 sm:py-4 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center flex-shrink-0">
                <EyeOffIcon className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">No Glare</p>
                <p className="text-xs text-slate-400 mt-0.5">Avoid reflections</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 sm:py-4 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Valid Documents</p>
                <p className="text-xs text-slate-400 mt-0.5">Current & not expired</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadPage;
